import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CommonUtilsService, dayjs, RootState } from '@umanforge/common';
import SelectInput from '@common_components/inputs/selects/selectInput.component';
import { Recurrence } from 'interfaces/recurrence.interface';
import WeekDaysSelect, { DaysToShow } from 'features/week_days_select/components/weekDaySelect.component';
import { GroupedOption } from '@common_components/inputs/selects/multiSelectInput.componentPages';
import DatePickerSoloInput from '@common_components/inputs/datePickerInputSolo.component';
import InputText from '@common_components/inputs/textInput.component';
import { SettingService } from 'services/setting.service';
import { DEFAULT_VALUES } from 'constants/calendar.constants';
import { useDispatch, useSelector } from 'react-redux';
import { ORDINALS } from 'constants/select.constants';
import { Tooltip } from 'react-tooltip';

interface RecurrenceModalProps {
  onSave: ( recurrence: Recurrence, isValid: boolean ) => void;
  onRecurrenceOpen: ( open: boolean ) => void
  sectorId: string;
  taskId?: string;
  recurrence?: Recurrence;
  taskDateStart: dayjs.Dayjs;
  isOpen?: boolean;
  disable?: boolean;
}

export type RecurrenceType = 'daily' | 'weekly' | 'monthly';
export interface RecurrenceError {
  endHours: string | undefined,
  duration: string | undefined,
  timeStart: string | undefined;
  timeEnd: string | undefined;
  recurrence: string | undefined;
}

const RecurrenceModal: React.FC<RecurrenceModalProps> = ( {
  onSave,
  onRecurrenceOpen,
  taskId,
  recurrence,
  taskDateStart,
  isOpen = false,
  disable = false,
  sectorId
} ) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => {return state.token.value;} );

  const [ type, setType ] = useState<RecurrenceType>( 'daily' );
  const [ selectedDays, setSelectedDays ] = useState<DaysToShow>( { sunday: false, monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false } );
  const [ repetitionSentence, setRepetitionSentence ] = useState<string | undefined>( undefined );
  const [ weekend, setWeekEnd ] = useState<boolean>( true );

  const [ repeat, setRepeat ] = useState( recurrence?.repeat ?? 1 );
  const [ repeatType, setRepeatType ] = useState( recurrence?.repeatType ?? 0 );
  const [ recurrenceModalOpen, setRecurrenceModalOpen ] = useState<boolean>( recurrence ? true : false );
  const [ monthlyDayNumber, setMonthlyDayNumber ] = useState<number>( recurrence?.type === 'monthly' && recurrence.repeatType === 1 ? ( recurrence.value ?? taskDateStart.date() ) : taskDateStart.date() );
  const [ monthlyWeekday, setMonthlyWeekday ] = useState<number>( recurrence?.type === 'monthly' && recurrence.repeatType === 2 ? dayjs( recurrence.dateStart ).day() : taskDateStart.day() );
  const [ monthlyWeekRank, setMonthlyWeekRank ] = useState<number>( recurrence?.type === 'monthly' && recurrence.repeatType === 2 ? ( recurrence.value === 0 ? 0 : ( recurrence.value ?? Math.max( 1, Math.min( 5, Math.ceil( taskDateStart.date() / 7 ) ) ) ) ) : 1 );

  const defaultTimeEnd = recurrence?.dateEnd ? dayjs( recurrence.dateEnd ) : taskDateStart.add( 2, 'month' );
  const defaultTimeStart = recurrence?.dateStart ? dayjs( recurrence.dateStart ) : taskDateStart;
  const [ dateEnd, setDateEnd ] = useState<dayjs.Dayjs>( defaultTimeEnd );
  const [ dateStart, setDateStart ] = useState<dayjs.Dayjs>( defaultTimeStart );

  const [ isFirstRender, setFirstRender ] = useState<boolean>( true );

  const [ recurrenceErrors, setRecurrenceErrors ] = useState<RecurrenceError>( {
    endHours: undefined,
    duration: undefined,
    timeStart: undefined,
    timeEnd: undefined,
    recurrence: undefined,
  } );


  useEffect( () => {
    init();
  }, [] );

  useEffect( () => {
    if ( recurrence ) {
      updateRecurrenceValues( recurrence );
    }
  }, [ recurrence ] );

  useEffect( () => {
    setRecurrenceModalOpen( isOpen );
  }, [ isOpen ] );

  useEffect( () => {
    if ( !isFirstRender && !disable ) {
      const nextStart = recurrence?.dateStart ? dayjs( recurrence.dateStart ) : taskDateStart;
      const nextEnd = recurrence?.dateEnd ? dayjs( recurrence.dateEnd ) : taskDateStart.add( 2, 'month' );
      setDateEnd( nextEnd );
      setDateStart( nextStart );
    }
  }, [ taskDateStart, recurrence ] );

  useEffect( () => {
    if ( !isFirstRender && !disable ) {
      handleChange();
    }
  }, [
    type,
    selectedDays,
    dateEnd,
    dateStart,
    repetitionSentence,
    repeat,
    repeatType,
    monthlyDayNumber,
    monthlyWeekday,
    monthlyWeekRank,
    isOpen
  ] );

  const toggleOpenRecurrence = () => {
    const open = !recurrenceModalOpen;
    setRecurrenceModalOpen( open );
    onRecurrenceOpen( open );
  };


  const init = async () => {
    const settings = await SettingService.getSectorSettingsBySector( sectorId, token, dispatch );
    setWeekEnd( ( settings?.showWeekend?.value ?? DEFAULT_VALUES.SHOWWEEKEND ) == 1 );

    updateRecurrenceValues( recurrence );
    handleChange();
    setFirstRender( false );
  };

  const checkRecurrence = (): RecurrenceError => {
    const _recurrenceErrors: RecurrenceError = {
      endHours: undefined,
      duration: undefined,
      timeStart: undefined,
      timeEnd: undefined,
      recurrence: undefined
    };

    const firstTaskStart = dateStart?.startOf( 'day' ).add( taskDateStart?.hour() ?? dayjs().hour(), 'hour' ).add( taskDateStart?.minute() ?? dayjs().minute(), 'minute' );

    if ( firstTaskStart.isBefore( dayjs() ) && !dateStart.isSame( dayjs( recurrence?.dateStart ), 'day' ) ) {
      _recurrenceErrors.endHours = t( 'translation:errors.you_cannot_create_task_in_the_past' );
    }
    if ( dateEnd.isBefore( dateStart ) ) {
      _recurrenceErrors.endHours = t( 'translation:errors.end_date_is_before_date_start' );
    }
    if ( dateEnd.diff( dateStart, 'month' ) > 6 ) {
      _recurrenceErrors.duration = t( 'translation:errors.the_recurrence_can_last_6_month_max' );
    }

    return _recurrenceErrors;
  };


  const updateRecurrenceValues = ( recc: Recurrence | undefined ) => {
    if ( recc ) {
      const days = valueToDays( recc.value );

      setType( recc.type as RecurrenceType );
      setSelectedDays( days );
      setDateStart( dayjs( recc.dateStart ) );
      setDateEnd( dayjs( recc.dateEnd ) );
      setRepeat( recc.repeat ?? 1 );
      setRepeatType( recc.repeatType ?? 1 );

      if ( recc.type === 'monthly' ) {
        if ( recc.repeatType === 2 ) {
          setMonthlyWeekRank( recc.value === 0 ? 0 : Math.min( 5, Math.max( 1, recc.value ) ) );
          setMonthlyWeekday( dayjs( recc.dateStart ).day() );
        }
        else {
          setMonthlyDayNumber( recc.value ?? taskDateStart.date() );
        }
      }
    }
    setRepetitionSentence( buildRepetitionSentence( selectedDays, type as RecurrenceType, dateStart, dateEnd, recurrenceErrors ) );
  };

  const valueToDays = ( value: number ) => {
    const weekMatrix = [ 0, 0, 0, 0, 0, 0, 0 ];
    for ( let i = 0; value; i++, value >>= 1 ) {
      if ( value & 1 ) { weekMatrix[ i ] = 1; };
    }
    return {
      monday: weekMatrix[ 1 ] != 0,
      tuesday: weekMatrix[ 2 ] != 0,
      wednesday: weekMatrix[ 3 ] != 0,
      thursday: weekMatrix[ 4 ] != 0,
      friday: weekMatrix[ 5 ] != 0,
      saturday: weekMatrix[ 6 ] != 0,
      sunday: weekMatrix[ 0 ] != 0
    };
  };

  const daysToValue = ( days: DaysToShow ) => {
    let res = 0;

    res += days.sunday ? 1 : 0;
    res += days.monday ? 2 : 0;
    res += days.tuesday ? 4 : 0;
    res += days.wednesday ? 8 : 0;
    res += days.thursday ? 16 : 0;
    res += days.friday ? 32 : 0;
    res += days.saturday ? 64 : 0;

    return res;
  };

  const getRepeatTypeFromData = () => {
    let _repeatType = repeatType;
    if ( type == 'weekly' ) {  _repeatType = 3; }
    else if ( type == 'daily' ) { _repeatType = 1; }
    return _repeatType;
  };

  const getLitteral = ( recurrenceType: string ): string => {
    let litteral = recurrenceType;
    switch ( recurrenceType ) {
    case 'daily': litteral = 'day'; break;
    case 'weekly': litteral = 'weekOfYear'; break;
    case 'monthly': litteral = 'month'; break;
    case 'yearly': litteral = 'year'; break;
    default: ;
    }
    try {
      const res =  new Intl.DisplayNames( dayjs.locale(), { type: 'dateTimeField' } ).of( litteral ) ?? litteral;
      return res[ 0 ].toUpperCase() + res.substring( 1 ).toLowerCase( );
    }
    catch ( err ) {
      console.warn( err );
      return t( litteral );
    }
  };

  const handleTypeChange = ( val: string ) => {
    setType( val as RecurrenceType );
    if ( val == 'weekly' ) {
      setRepeatType( 3 );
    }
    else if ( val == 'daily' || ( val == 'monthly' && repeatType == 0 || repeatType == 3 ) ) {
      setRepeatType( 1 );
    }
  };

  const getMonthlyWeekdayAnchor = ( baseDate: dayjs.Dayjs, weekday: number, occurrence: number ): dayjs.Dayjs => {
    const monthStart = baseDate.startOf( 'month' );

    if ( occurrence === 0 ) {
      const monthEnd = baseDate.endOf( 'month' );
      const monthEndShift = ( monthEnd.day() - weekday + 7 ) % 7;
      return monthEnd.subtract( monthEndShift, 'day' );
    }

    const firstWeekdayOffset = ( weekday - monthStart.day() + 7 ) % 7;
    const firstWeekday = monthStart.add( firstWeekdayOffset, 'day' );
    return firstWeekday.add( ( occurrence - 1 ) * 7, 'day' );
  };

  const getMonthlyWeekRankOptions = (): Promise<GroupedOption[]> => {
    return Promise.resolve( [ {
      options: [
        { id: '1', label: t( 'first' ) },
        { id: '2', label: t( 'second' ) },
        { id: '3', label: t( 'third' ) },
        { id: '4', label: t( 'fourth' ) },
        { id: '5', label: t( 'fifth' ) },
        { id: '0', label: t( 'last' ) },
      ]
    } ] );
  };

  const getMonthlyWeekdayOptions = (): Promise<GroupedOption[]> => {
    const options = [
      { id: '1', label: dayjs().day( 1 ).format( 'dddd' ) },
      { id: '2', label: dayjs().day( 2 ).format( 'dddd' ) },
      { id: '3', label: dayjs().day( 3 ).format( 'dddd' ) },
      { id: '4', label: dayjs().day( 4 ).format( 'dddd' ) },
      { id: '5', label: dayjs().day( 5 ).format( 'dddd' ) },
      { id: '6', label: dayjs().day( 6 ).format( 'dddd' ) },
      { id: '0', label: dayjs().day( 0 ).format( 'dddd' ) },
    ];

    return Promise.resolve( [ { options } ] );
  };

  const getValueFromData = () => {
    switch ( type ) {
    case 'daily': return 0;
    case 'weekly': return daysToValue( selectedDays );
    case 'monthly':
      if ( repeatType === 2 ) {
        return monthlyWeekRank;
      }
      return monthlyDayNumber;
    default: return 0;
    }
  };

  const handleChange = () => {
    const recurrenceDateStart = type === 'monthly' && repeatType === 2 ? getMonthlyWeekdayAnchor( dateStart, monthlyWeekday, monthlyWeekRank ) : dateStart;
    const rec = {
      type: type,
      dateEnd: dateEnd.toISOString(),
      dateStart: recurrenceDateStart.toISOString(),
      value: getValueFromData(),
      idTask: taskId,
      repeat: repeat,
      repeatType: getRepeatTypeFromData()
    } as Recurrence;
    const errors = checkRecurrence();
    setRecurrenceErrors( errors );
    setRepetitionSentence( buildRepetitionSentence( selectedDays, type as RecurrenceType, dateStart, dateEnd, errors ) );
    onSave( rec, !errors.endHours && !errors.duration );
  };

  const buildRepetitionSentence = ( days: DaysToShow, _type: RecurrenceType, _dateStart: dayjs.Dayjs, _dateEnd: dayjs.Dayjs, errors: RecurrenceError ) => {
    if ( !days || !_type || !_dateEnd || !_dateStart || errors.duration || errors.endHours ) {
      return undefined;
    }
    return `${ t( 'translation:the_task_will_repeat_from' ) } ${ _dateStart.format( 'LL' ) } ${ t( 'translation:until' )} ${ _dateEnd.format( 'LL' )}`;
  };

  // eslint-disable-next-line require-await
  const getRecurrenceType = async (): Promise<GroupedOption[]> => {
    const selectOptions = objectToGroupedOption(  [ 'daily', 'weekly', 'monthly' ] );
    return selectOptions;
  };

  const objectToGroupedOption = ( values: string[] ): GroupedOption[] => {
    if ( values.length > 0 ) {
      return [ { options: CommonUtilsService.sortByKey(
        values.map( value => { return { label: t( value ), id: value }; } ),
        'label',
        'asc'
      ) } ];
    }
    return [];
  };

  // Render
  return (
    <div>
      <div className={`w-full flex ${ recurrenceModalOpen ? 'flex-col-reverse' : 'flex-col'}`} >
        <div className={ 'lg:flex gap-x-5' }>
          <div className={ `w-full ${ recurrenceModalOpen ? '' : 'hidden' }` }>
            <div className={ 'flex' }>
              <label className={'w-3/12 text-gray-600 mr-4 left mt-4 flex'}>
                { t( 'translation:end_of_recurrence' ) }
                <>
                  <span
                    id={'roletask-info'}
                    className={'ml-2 cursor-pointer flex items-center justify-center rounded-full bg-gray-200/60 w-6 h-6 text-gray-700 text-sm font-bold'}
                  > i </span>
                  <Tooltip
                    anchorSelect={'#roletask-info'}
                    className={'z-[1000] max-w-[400px] text-wrap'}
                    place="top"
                    positionStrategy='fixed'
                  >
                    <p className="pt-1">{t( 'translation:recurrence_end_description' )}</p>
                  </Tooltip>
                </>
              </label>
              <div className='flex gap-x-2 my-2 w-full'>
                <div className='w-full'>
                  <DatePickerSoloInput
                    defaultValue={ defaultTimeEnd }
                    onChange={ val => { if ( val ) {
                      setDateEnd( val );
                    } } }
                    dateFormat='L'
                    disable={ disable }
                    minDate={ dateStart.startOf( 'day' ) }
                    placeholder={ t( 'translation:no_end' )}
                    canCancel
                  />
                </div>
              </div>
            </div>
            { recurrenceErrors.duration && <p className="text-red-500 h-6 -mt-2">{ recurrenceErrors.duration }</p> }
            { recurrenceErrors.endHours && <p className="text-red-500 h-6 -mt-2">{ recurrenceErrors.endHours }</p> }
          </div>
        </div>

        <div className='flex w-1/3 h-fit' id={'recurrence_form_check'} >
          <p className={'text-gray-600 w-full flex items-center left'}> { t( 'translation:recurrence' ) } </p>
          <div className='w-full flex items-center'>
            <div className='w-full px-3' >
              <div className="px-2 pt-2 right w-full justify-end flex items-center">
                <label className="w-full flex-center items-center cursor-pointer" onClick={ toggleOpenRecurrence }>
                  <input
                    type="checkbox"
                    value=""
                    className="sr-only peer"
                    name={'day_switch'}
                    checked={ recurrenceModalOpen }
                    readOnly
                    disabled={disable}
                  />
                  <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
          <p className="h-6 -mt-1"/>
        </div>
      </div>


      <div className={ 'min-h-44 ' + ( recurrenceModalOpen ? '' : ' hidden' ) }>
        <div className='flex flex-col gap-3 w-full'>
          <div className='flex w-full items-center gap-4'>
            <p className={'text-gray-600 whitespace-nowrap left'}> { t( 'translation:repeat_each' ) } </p>
            <InputText
              type='number'
              min={1}
              max={10}
              isRequired
              value={ repeat }
              onChange={ ( val: string ) => { setRepeat( Number.parseInt( val ) ?? 0 ); } }
              className='w-1/3'
              placeholder={ t( 'translation:repetition' ) }
              disable={disable}
            />
            { disable ?
              <InputText
                value={ getLitteral( type ) }
                disable
              />
              :
              <SelectInput
                className='w-full'
                placeholder={ t( 'translation:repetition' ) }
                fetchOptions={ getRecurrenceType }
                onSelect={ handleTypeChange }
                selected={ type }
                isRequired
                isDisable={ disable }
              />
            }
          </div>
        </div>


        { ( type == 'monthly' ) &&
            <>
              <div className="flex items-center mb-3 mt-4 w-fit p-1 pr-5" onClick={ e => { e.stopPropagation(); if ( !disable ) { setRepeatType( 1 ); } } }>
                <input
                  id="default-radio-1"
                  type="radio"
                  name="default-radio"
                  value=""
                  checked={ repeatType == 1 }
                  className="w-4 h-4 text-neutral-primary border-default-medium bg-neutral-secondary-medium rounded-full checked:border-brand focus:outline-none"/>
                <label htmlFor="default-radio-1" className="select-none ms-2 text-gray-700">
                  { t( 'recurrence_monthly_day_of_the_month' ) }
                </label>
                { !disable && (
                  <InputText
                    type='number'
                    min={1}
                    max={31}
                    isRequired
                    value={ monthlyDayNumber }
                    onChange={ ( val: string ) => { setMonthlyDayNumber( Number.parseInt( val ) ?? 1 ); } }
                    className='w-1/3 min-w-[120px] max-w-[140px] ml-2'
                    placeholder={ t( 'translation:repetition' ) }
                    disable={ disable }
                  />
                ) }
                { !disable && <span className="ml-2 text-gray-700">{ t( 'recurrence_monthly_of_the_month' ) }</span> }
              </div>

              <div className="flex flex-col gap-2 mb-3 mt-4 w-full p-1 pr-5" onClick={ e => { e.stopPropagation(); if ( !disable ) { setRepeatType( 2 ); } } }>
                <div className="flex items-center flex-wrap gap-2">
                  <input
                    value=""
                    checked={ repeatType == 2 }
                    id="default-radio-2"
                    type="radio"
                    name="default-radio"
                    className="w-4 h-4 text-neutral-primary border-default-medium bg-neutral-secondary-medium rounded-full checked:border-brand focus:outline-none"/>
                  <label htmlFor="default-radio-2" className="select-none text-gray-700">
                    { t( 'recurrence_monthly_on_the' ) }
                  </label>

                  {!disable && (
                    <SelectInput
                      className="w-32"
                      selected={ `${ monthlyWeekRank }` }
                      onSelect={ value => { setMonthlyWeekRank( Number( value ) ); } }
                      fetchOptions={ getMonthlyWeekRankOptions }
                      isRequired
                      isDisable={ disable }
                    />
                  ) }

                  {!disable && (
                    <SelectInput
                      className="w-44"
                      selected={ `${ monthlyWeekday }` }
                      onSelect={ value => { setMonthlyWeekday( Number( value ) ); } }
                      fetchOptions={ getMonthlyWeekdayOptions }
                      isRequired
                      isDisable={ disable }
                    />
                  ) }

                  { disable && (
                    <span className="ml-2 text-gray-700">
                      { monthlyWeekRank === 0 ? t( 'last' ) : t( ORDINALS[ monthlyWeekRank ] ?? `${ monthlyWeekRank }` ) } { dayjs().day( monthlyWeekday ).format( 'dddd' ) }
                    </span>
                  ) }
                  <span className="ml-2 text-gray-700">{ t( 'recurrence_monthly_of_the_month' ) }</span>
                </div>
              </div>
            </>
        }

        <div className={`mt-4 ${ ( type === 'weekly' ) ? '' : 'hidden'} `}>
          <WeekDaysSelect
            label={ t( 'translation:recurrence' ) }
            onSelectionChange={ setSelectedDays }
            labelPosition='left'
            defaultSelectedDays={ selectedDays }
            shownDays={ weekend ?
              { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: true, sunday: true } :
              { monday: true, tuesday: true, wednesday: true, thursday: true, friday: true, saturday: false, sunday: false }
            }
            canBeEmpty={false}
            disable={disable}
          />
          { recurrenceErrors.recurrence && <p className="text-red-500 h-6">{ recurrenceErrors.recurrence }</p> }
        </div>
        { repetitionSentence && (
          <p className="mt-2 h-fit max-w-full overflow-wrap-anywhere whitespace-normal break-words text-gray-700">
            { repetitionSentence }
          </p>
        ) }
      </div>
    </div>
  );
};

export default RecurrenceModal;
